import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ShareIcon from '@mui/icons-material/Share';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import Iconify from '../components/Iconify';
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import NewRecordingShareModal from '../components/NewRecordingShareModal';
import ShareRecordingDialog from '../components/ShareRecordingDialog';
import { requestErrorMessage } from '../components/useRecordingPublish';
import { baseUrl, s3ToObjectUrl } from '../constant/config';
import { AuthState } from '../context/auth';

const PAGE_SIZE = 15;

// How often to look for recordings from sessions that just finished.
const POLL_MS = 60000;

// Set when the admin turns the prompt off for good. Everything else about the
// prompt is per-visit, so it reappears each time the page is opened.
const MUTED_KEY = 'turn2speak_mute_share_prompt';

// Left over from an earlier version that suppressed the prompt across visits.
const LEGACY_SEEN_KEY = 'turn2speak_prompted_recordings';

// Recording durations come back in seconds.
const formatDuration = (seconds) => {
  if (!seconds) return '-';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

// The stored path looks like
//   s3://turntospeak/hls/<room>/<date>/<ts>/file-recording/Rec-<room>-<ts>-0.mp4
const fileName = (path) => (path ? path.split('/').pop() : '-');

// One icon per platform this recording was successfully published to.
function SharedBadges({ shares }) {
  const published = (shares || []).filter((s) => s.status === 'published');
  if (published.length === 0) return <span style={{ opacity: 0.35 }}>-</span>;

  const seen = [];
  return (
    <Stack direction="row" spacing={0.5} justifyContent="center">
      {published.map((s) => {
        if (seen.includes(s.platform)) return null;
        seen.push(s.platform);
        const Icon = s.platform === 'facebook' ? FacebookIcon : InstagramIcon;
        const color = s.platform === 'facebook' ? '#1877F2' : '#E1306C';
        const icon = <Icon key={s.platform} sx={{ color, fontSize: 18 }} />;
        return (
          <Tooltip key={s.platform} title={`Published to ${s.platform} on ${formatDate(s.sharedAt)}`}>
            {s.permalink ? (
              <a href={s.permalink} target="_blank" rel="noreferrer" style={{ lineHeight: 0 }}>
                {icon}
              </a>
            ) : (
              <span style={{ lineHeight: 0 }}>{icon}</span>
            )}
          </Tooltip>
        );
      })}
    </Stack>
  );
}

export default function Recordings() {
  const { topics } = AuthState();
  const [topicId, setTopicId] = useState('');
  const [page, setPage] = useState(0);
  const [playing, setPlaying] = useState(null);
  const [sharing, setSharing] = useState(null);
  const [copied, setCopied] = useState(null);
  const [prompted, setPrompted] = useState(null);
  // Per-visit only: reset on every mount so opening the page prompts again.
  const [seen, setSeen] = useState(() => new Set());
  // Set when the selected recording's file cannot be loaded.
  const [playError, setPlayError] = useState(null);

  const {
    data: videos,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery(
    ['recordings', topicId, page],
    async () => {
      const params = new URLSearchParams({ limit: PAGE_SIZE, skip: page * PAGE_SIZE });
      if (topicId) params.append('topic', topicId);
      const { data } = await axios.get(`${baseUrl}/api/videos?${params.toString()}`);
      return data.data;
    },
    {
      refetchOnWindowFocus: false,
      // Reload by itself when the connection comes back.
      refetchOnReconnect: true,
      keepPreviousData: true,
      // Poll so a session that finishes while this page is open surfaces on
      // its own rather than waiting for a manual refresh.
      refetchInterval: POLL_MS,
    }
  );

  const topicName = (id) => {
    const match = topics?.find((t) => t._id === id || t._id === id?._id);
    return match ? match.name : '-';
  };

  useEffect(() => {
    try {
      localStorage.removeItem(LEGACY_SEEN_KEY);
    } catch {
      // Nothing to clean up.
    }
  }, []);

  const handleCopy = (path) => {
    // Copy the raw s3:// URI - that is what is useful in the AWS console.
    navigator.clipboard?.writeText(path);
    setCopied(path);
    setTimeout(() => setCopied(null), 1500);
  };

  const handleTopicChange = (e) => {
    setTopicId(e.target.value);
    setPage(0);
  };

  const rows = useMemo(() => videos || [], [videos]);

  // Prompt once per visit for the newest recording that has not been published
  // yet, and again if a session finishes while the page is open. Only the
  // unfiltered first page is considered, so a topic filter cannot trigger it.
  useEffect(() => {
    if (!rows.length) return;
    if (topicId || page !== 0) return;
    if (prompted || sharing || playing) return;
    if (localStorage.getItem(MUTED_KEY) === '1') return;

    const candidate = rows.find(
      (r) => !seen.has(r._id) && !(r.shares || []).some((sh) => sh.status === 'published')
    );

    // Record every row currently loaded, not just the one being prompted -
    // otherwise dismissing would immediately re-prompt for the next row and
    // walk the admin through the whole page one modal at a time.
    const next = new Set(seen);
    rows.forEach((r) => next.add(r._id));
    if (next.size !== seen.size) setSeen(next);

    if (candidate) setPrompted(candidate);
  }, [rows, seen, topicId, page, prompted, sharing, playing]);

  const dismissPrompt = () => setPrompted(null);

  const dismissForever = () => {
    try {
      localStorage.setItem(MUTED_KEY, '1');
    } catch {
      // Private browsing - the prompt simply stays on.
    }
    setPrompted(null);
  };
  return (
    <Page title="Recordings">
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Debate recordings
          </Typography>
          <TextField
            select
            size="small"
            label="Topic"
            value={topicId}
            onChange={handleTopicChange}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">All topics</MenuItem>
            {topics?.map((t) => (
              <MenuItem key={t._id} value={t._id}>
                {t.name}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <Card>
          <Scrollbar>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 900 }} aria-label="debate recordings">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Debate</TableCell>
                    <TableCell>Topic</TableCell>
                    <TableCell>Recorded</TableCell>
                    <TableCell align="center">Length</TableCell>
                    <TableCell align="center">Files</TableCell>
                    <TableCell>File</TableCell>
                    <TableCell align="center">Shared</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, i) => (
                    <TableRow key={row._id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell component="th" scope="row">
                        {page * PAGE_SIZE + i + 1}
                      </TableCell>
                      <TableCell
                        className="one_line_text"
                        style={{ minWidth: '160px', maxWidth: '220px' }}
                        title={row.debate?.title}
                      >
                        {row.debate?.title || <span style={{ opacity: 0.5 }}>Debate removed</span>}
                      </TableCell>
                      <TableCell style={{ minWidth: '120px' }}>{topicName(row.topic)}</TableCell>
                      <TableCell style={{ minWidth: '150px' }}>{formatDate(row.createdAt)}</TableCell>
                      <TableCell align="center">{formatDuration(row.duration)}</TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={row.layers?.length || 0} />
                      </TableCell>
                      <TableCell
                        className="one_line_text"
                        style={{ minWidth: '160px', maxWidth: '240px' }}
                        title={row.path}
                      >
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {fileName(row.path)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <SharedBadges shares={row.shares} />
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Share">
                          <IconButton size="small" onClick={() => setSharing(row)}>
                            <ShareIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Play">
                          <IconButton size="small" onClick={() => setPlaying(row)}>
                            <PlayCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={copied === row.path ? 'Copied' : 'Copy S3 path'}>
                          <IconButton size="small" onClick={() => handleCopy(row.path)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Open in new tab">
                          <IconButton
                            size="small"
                            component="a"
                            href={s3ToObjectUrl(row.path)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}

                  {!isLoading && rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                        <Typography variant="subtitle1" gutterBottom>
                          No recordings found
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {topicId
                            ? 'This topic has no completed recordings yet.'
                            : 'Recordings appear here once a debate finishes and the sync job has run.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}

                  {isLoading && (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                        <Typography variant="body2">Loading recordings...</Typography>
                      </TableCell>
                    </TableRow>
                  )}

                  {isError && (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                        <Typography variant="body2" color="error" gutterBottom>
                          Could not load recordings. {requestErrorMessage(error)}
                        </Typography>
                        <Button size="small" variant="outlined" onClick={() => refetch()} disabled={isFetching}>
                          {isFetching ? 'Retrying…' : 'Retry'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} sx={{ p: 2 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', mr: 1 }}>
              Page {page + 1}
            </Typography>
            <Button
              size="small"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              startIcon={<Iconify icon="eva:arrow-ios-back-fill" />}
            >
              Previous
            </Button>
            <Button
              size="small"
              disabled={rows.length < PAGE_SIZE}
              onClick={() => setPage((p) => p + 1)}
              endIcon={<Iconify icon="eva:arrow-ios-forward-fill" />}
            >
              Next
            </Button>
          </Stack>
        </Card>
      </Container>

      <NewRecordingShareModal
        open={Boolean(prompted)}
        video={prompted}
        onClose={dismissPrompt}
        onDismissForever={dismissForever}
      />

      <ShareRecordingDialog open={Boolean(sharing)} onClose={() => setSharing(null)} video={sharing} />

      <Dialog
        open={Boolean(playing)}
        onClose={() => {
          setPlaying(null);
          setPlayError(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{playing?.debate?.title || 'Recording'}</DialogTitle>
        <DialogContent>
          {playing && (
            <Box>
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <video
                src={s3ToObjectUrl(playing.path)}
                controls
                autoPlay
                onError={() =>
                  setPlayError(
                    navigator.onLine === false
                      ? "You're offline, so the video can't load. Reopen it when your connection is back."
                      : 'The video could not be loaded. Your connection may be too slow - close and try again.'
                  )
                }
                onLoadedData={() => setPlayError(null)}
                style={{ width: '100%', borderRadius: 8, background: '#000' }}
              />
              {playError && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {playError}
                </Typography>
              )}
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 2, wordBreak: 'break-all', color: 'text.secondary' }}
              >
                {playing.path}
              </Typography>

              {playing.layers?.length > 1 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Other files from this session ({playing.layers.length})
                  </Typography>
                  <Stack spacing={0.5}>
                    {playing.layers.map((layer) => (
                      <Typography
                        key={layer}
                        variant="caption"
                        component="a"
                        href={s3ToObjectUrl(layer)}
                        target="_blank"
                        rel="noreferrer"
                        sx={{ wordBreak: 'break-all' }}
                      >
                        {fileName(layer)}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Page>
  );
}
